/**
 * Domain Model: Program Form
 * Business entities and types for program creation form.
 */

/**
 * Incentive rule type for program configuration
 */
export type IncentiveRuleType = "general" | "product";

/**
 * Customer tier levels
 */
export type TierType = "bronze" | "silver" | "gold" | "platinum";

/**
 * Product incentive data structure for product-based incentive rules
 */
export type ProductIncentiveData = {
  readonly productId: string;
  readonly productName: string;
  readonly productDescription?:string;
  readonly pointType?: string;
  readonly pointIncentive: number;
};

/**
 * Customer data structure for customer segment targeting
 */
export type CustomerData = {
  readonly customerId: string;
  readonly phoneNumber: string;
  readonly name: string;
  readonly area: string;
  readonly tier: string;
};

/**
 * Base reward level for Duwit channel
 */
export type BaseRewardLevel = {
  readonly id: string;
  readonly minTransaction: number;
  readonly maxTransaction: number;
  readonly pointsEarned: number;
};

/**
 * Complete form data structure for program creation
 */
export type ProgramFormData = {
  // Step 1 - Event Trigger
  readonly programName: string;
  readonly channel: string;
  readonly startDate: string;
  readonly endDate: string;
  // Step 2 - Incentive Rule (non-Duwit)
  readonly incentiveRule: IncentiveRuleType;
  readonly basedOn: string;
  readonly minTransaction: string;
  readonly maxTransaction: string;
  readonly productIncentiveFile: string;
  readonly productIncentiveList: ProductIncentiveData[];
  readonly productTemporaryFileId: string;
  readonly productTotalCount: number;
  // Step 2 - Incentive Rule (Duwit-specific)
  readonly baseRewardLevels: BaseRewardLevel[];
  readonly maxBaseRewardPointsPerDay: number;
  readonly stampMinimumTransaction: number;
  readonly weeklyStampMinDays: number;
  readonly weeklyStampMultiplier: number;
  readonly monthlyStampMinDays: number;
  readonly monthlyStampMultiplier: number;
  // Step 3 - Customer Segment
  readonly area: string;
  readonly tiers: TierType[];
  readonly whitelistFile: string;
  readonly customerList: CustomerData[];
  readonly whitelistTemporaryId: string;
  readonly whitelistTotalCount: number;
};

/**
 * Result type for CSV parsing operations
 */
export type CSVParseResult = {
  readonly data: ProductIncentiveData[];
  readonly error: string | null;
};

/**
 * Initial form data values
 */
export const initialProgramFormData: ProgramFormData = {
  // Step 1
  programName: "",
  channel: "",
  startDate: "",
  endDate: "",
  // Step 2 (non-Duwit)
  incentiveRule: "general",
  basedOn: "",
  minTransaction: "",
  maxTransaction: "",
  productIncentiveFile: "",
  productIncentiveList: [],
  productTemporaryFileId: "",
  productTotalCount: 0,
  // Step 2 (Duwit-specific)
  baseRewardLevels: [],
  maxBaseRewardPointsPerDay: 0,
  stampMinimumTransaction: 0,
  weeklyStampMinDays: 0,
  weeklyStampMultiplier: 0,
  monthlyStampMinDays: 0,
  monthlyStampMultiplier: 0,
  // Step 3
  area: "",
  tiers: [],
  whitelistFile: "",
  customerList: [],
  whitelistTemporaryId: "",
  whitelistTotalCount: 0,
};

